import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { User } from '../../models/user';
import { AuthRepository } from '../../domain/auth/auth.repository';
import { AuthCredentialsDto, ChangePasswordDto, ForgotPasswordDto, ConfirmForgotPasswordDto, CompleteNewPasswordDto, UserDetailsDto } from '../../domain/_shared/dto';
import { PermissionService } from '../permission/permission.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
	private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
	private _whenLoggedIn = new BehaviorSubject<User|null>(null);
	private _user: User | null = null;
	private _hostToken = new BehaviorSubject<User|null>(null);

	constructor(
		private http: HttpClient,
		private cognitoRepository: AuthRepository,
		private permissionService: PermissionService
	) {
		this.isAuthenticated().subscribe({
			next: (isAuthenticated) => {
				if (isAuthenticated) {
					this.initAWSCredentials();
				}
			}
		})
	}

	logout(): void {
		this.cognitoRepository.clearCredentials();
		this.isAuthenticatedSubject.next(false);
	}

	isAuthenticated(): Observable<boolean> {
		const token = localStorage.getItem('a_token');
		const webUserToken = localStorage.getItem('token');

    if (!token || token === 'undefined' || !webUserToken || webUserToken === 'undefined') {
      console.log('No token found');
			return of(false);
		}

		return new Observable<boolean>(observer => {
			this.cognitoRepository.isSessionValid().then(
				isValid => {
					observer.next(isValid);
					observer.complete();
				},
				err => {
					observer.next(false);
					observer.complete();
				}
			);
		});
	}

	public get whenLoggedIn(): BehaviorSubject<User|null> {
		return this._whenLoggedIn;
	}

	public get user(): User | null{
		return this._user;
	}

	public changePassword(username: string, oldPassword: string, newPassword: string, requireToken: boolean): Promise<void> {
		const dto: ChangePasswordDto = {
			username,
			oldPassword,
			newPassword,
			requireToken
		};

		return this.cognitoRepository.changePassword(dto).then(() => {
			this.isAuthenticatedSubject.next(true);
		});
	}

	public async initAWSCredentials() {
		const cognitoUser = this.cognitoRepository.getCurrentUser();
		if (!cognitoUser) {
			return;
		}

		this.setUser(new User(cognitoUser));
		await this.cognitoRepository.initAWSCredentials();
	}

	getUserSession(): Promise<CognitoUserSession> {
		return this.cognitoRepository.getSession();
	}

	public clearCredentials() {
		this.cognitoRepository.clearCredentials();
		this.setUser(null);
		this.setToken(null);
	}

	private setUser(user: User | null) {
		this._user = user
		this._whenLoggedIn.next(user);
	}

	setToken(value:any) {
		this._hostToken.next(value);
	}

	public checkAWSCredentials(callback: (err?:any) => void) {
		this.cognitoRepository.checkAWSCredentials()
			.then(() => callback())
			.catch(err => callback(err));
	}

    public forgotPassword(username: string): Promise<any> {
        const dto: ForgotPasswordDto = { username };
        return this.cognitoRepository.forgotPassword(dto);
    }

    public confirmForgotPassword(username: string, verificationCode: string, newPassword: string): Promise<string> {
        const dto: ConfirmForgotPasswordDto = { username, verificationCode, newPassword };
        return this.cognitoRepository.confirmForgotPassword(dto);
    }

    public completeNewPasswordChallenge(cognitoUser: any, newPassword: string, userAttributes: any): Promise<CognitoUserSession> {
        const dto: CompleteNewPasswordDto = { cognitoUser, newPassword, userAttributes };
        return this.cognitoRepository.completeNewPasswordChallenge(dto);
    }

    public authenticateUser(username: string, password: string): Promise<any> {
        const dto: AuthCredentialsDto = { username, password };
        return this.cognitoRepository.authenticateUser(dto);
    }
  
  public saveUserDetails(res: any) {
      const dto = res.success.results[0];
      localStorage.setItem('web_user', JSON.stringify(dto.user_details));
      localStorage.setItem('settings', JSON.stringify(dto.settings));
      localStorage.setItem('permissions', JSON.stringify(dto.permissions));
      this.permissionService.setPermissions(dto.permissions);
      localStorage.setItem('role_code', dto.user_details.role_code);
      localStorage.setItem('token', res.token); // token is at root level
    }
}
